import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../services/api'

// Struttura risposta API backend
interface ApiDashboardStats {
  locali: { totale: number; attivi: number }
  utenti: { totale: number }
  segnalazioni: { nuove: number; totale: number }
  visite: number
  ultimiLocali: Array<{ id: number; nome: string; createdAt: string }>
  ultimeSegnalazioni: Array<{ id: number; nomeLocale: string; stato: string; createdAt: string }>
}

interface DashboardStats {
  totaleLocali: number
  localiAttivi: number
  totaleUtenti: number
  segnalazioniNuove: number
  visiteTotali: number
}

/**
 * Dashboard admin con statistiche
 */
export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await api.get<ApiDashboardStats>('/admin/dashboard/stats')
        // Trasforma la risposta API nel formato atteso dal frontend
        setStats({
          totaleLocali: data?.locali?.totale ?? 0,
          localiAttivi: data?.locali?.attivi ?? 0,
          totaleUtenti: data?.utenti?.totale ?? 0,
          segnalazioniNuove: data?.segnalazioni?.nuove ?? 0,
          visiteTotali: data?.visite ?? 0,
        })
      } catch (error) {
        console.error('Errore caricamento stats:', error)
        // Fallback stats
        setStats({
          totaleLocali: 0,
          localiAttivi: 0,
          totaleUtenti: 0,
          segnalazioniNuove: 0,
          visiteTotali: 0,
        })
      } finally {
        setLoading(false)
      }
    }
    fetchStats()
  }, [])

  const statCards = [
    {
      label: 'Locali Totali',
      value: stats?.totaleLocali || 0,
      icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z',
      color: 'gold',
      link: '/admin/locali',
    },
    {
      label: 'Locali Attivi',
      value: stats?.localiAttivi || 0,
      icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      color: 'green',
      link: '/admin/locali',
    },
    {
      label: 'Utenti Registrati',
      value: stats?.totaleUtenti || 0,
      icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
      color: 'blue',
      link: '/admin/utenti',
    },
    {
      label: 'Segnalazioni Nuove',
      value: stats?.segnalazioniNuove || 0,
      icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
      color: 'orange',
      link: '/admin/segnalazioni',
    },
  ]

  const colorClasses: Record<string, string> = {
    gold: 'bg-gold/10 text-gold',
    green: 'bg-green-500/10 text-green-400',
    blue: 'bg-blue-500/10 text-blue-400',
    orange: 'bg-orange-500/10 text-orange-400',
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-night-light border border-gray-800 rounded-xl p-6 animate-pulse">
            <div className="h-12 w-12 bg-gray-700 rounded-lg mb-4"></div>
            <div className="h-8 w-20 bg-gray-700 rounded mb-2"></div>
            <div className="h-4 w-32 bg-gray-700 rounded"></div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map(card => (
          <Link
            key={card.label}
            to={card.link}
            className="bg-night-light border border-gray-800 rounded-xl p-6 hover:border-gray-700 transition-colors"
          >
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${colorClasses[card.color]}`}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={card.icon} />
              </svg>
            </div>
            <p className="font-display text-3xl text-white mb-1">{card.value}</p>
            <p className="text-gray-400 text-sm">{card.label}</p>
          </Link>
        ))}
      </div>

      {/* Quick actions */}
      <div className="bg-night-light border border-gray-800 rounded-xl p-6">
        <h2 className="font-display text-xl text-white mb-4">Azioni Rapide</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/admin/locali/nuovo"
            className="flex items-center gap-3 p-4 bg-gold/10 border border-gold/30 rounded-lg hover:bg-gold/20 transition-colors"
          >
            <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="text-gold font-medium">Aggiungi Locale</span>
          </Link>
          <Link
            to="/admin/segnalazioni"
            className="flex items-center gap-3 p-4 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <span className="text-gray-300 font-medium">Gestisci Segnalazioni</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-3 p-4 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span className="text-gray-300 font-medium">Vedi Sito</span>
          </Link>
        </div>
      </div>
    </div>
  )
}
